import type { Feature, Polygon, MultiPolygon, Position } from "geojson";
import { lineString, featureCollection } from "@turf/helpers";
import turfBuffer from "@turf/buffer";
import booleanIntersects from "@turf/boolean-intersects";
import difference from "@turf/difference";
import type { GeometryResult } from "../types";

/** Default buffer in meters — creates separation to prevent touching boundaries */
const DEFAULT_BUFFER_METERS = 1;

/**
 * Cut a polygon into two or more parts using a cutting line.
 * Accepts GeoJSON in EPSG:4326 (lng/lat) — no projection conversion needed.
 *
 * @param poly The polygon to cut
 * @param cuttingLine Array of [lng, lat] positions forming the cutting line
 * @param bufferMeters Buffer distance in meters (default 1m)
 * @returns MultiPolygon of resulting parts, or error
 */
export function cutPolygon(
	poly: Feature<Polygon>,
	cuttingLine: Position[],
	bufferMeters?: number,
): GeometryResult<Feature<Polygon | MultiPolygon>> {
	if (cuttingLine.length < 2) {
		return { ok: false, error: "Cutting line must have at least 2 points" };
	}

	const line = lineString(cuttingLine);

	if (!booleanIntersects(line, poly)) {
		return { ok: false, error: "Cutting line does not intersect polygon" };
	}

	const buffer = bufferMeters ?? DEFAULT_BUFFER_METERS;

	// Convert buffer distance from meters to degrees (approximate at mid-latitude)
	// At ~40° latitude, 1 meter ≈ 0.000009 degrees
	const bufferDegrees = buffer / 111320;

	const bufferedLine = turfBuffer(line, bufferDegrees, { units: "degrees" });
	if (!bufferedLine) {
		return { ok: false, error: "Failed to create buffered cutting line" };
	}

	// Split the polygon by removing the buffer zone
	// This creates two polygons separated by the buffer distance
	const splitResult = difference(featureCollection([poly, bufferedLine]));
	if (!splitResult) {
		return { ok: false, error: "Failed to split polygon" };
	}

	return {
		ok: true,
		data: splitResult as Feature<Polygon | MultiPolygon>,
	};
}
